/**
 * Override queue + recorded OverrideRecord list (§5.5 / §5.6 PLATFORM-SPEC).
 *
 * Queue membership is the existing `tasks.human_gated` door (abstain-route
 * escalates through `escalateToHuman` → human_gated=1). The write path is NOT
 * here: the single door remains `taskActions.setStatus` / `verdicts.submit` →
 * `override.human` via review-decision.ts. Both ops are list-only.
 */

import { z } from "zod";
import type { Operation } from "../types";

const overrideQueueItemSchema = z.object({
  taskId: z.number().int().positive(),
  title: z.string(),
  status: z.string(),
  projectSlug: z.string(),
  projectName: z.string(),
  sectionSlug: z.string(),
  sectionName: z.string(),
  /** 1 when tasks.human_gated is set (primary queue membership). */
  humanGated: z.number().int(),
  /** Severity / consequence stratum when known (nullable). */
  severity: z.string().nullable(),
  kind: z.string().nullable(),
  /** Escalation reasons mined from the [human-sign-off] description note. */
  reasons: z.array(z.string()),
  /** Short description excerpt for the raw presentation arm. */
  descriptionExcerpt: z.string().nullable(),
  /** Before half for override-as-diff (current state). */
  before: z.object({
    status: z.string(),
    human_gated: z.number().int(),
    title: z.string(),
  }),
  /**
   * Proposed after half on accept (done + gate cleared). Reject uses
   * in_progress + gate cleared — the client flips path, not this payload.
   */
  afterAccept: z.object({
    status: z.string(),
    human_gated: z.number().int(),
    title: z.string(),
  }),
  updatedAt: z.string().nullable(),
  /** True when status is already done but still gated (#44753 class strand). */
  strandedDone: z.boolean(),
});
export type OverrideQueueItem = z.infer<typeof overrideQueueItemSchema>;

const listQuery = z.object({
  /** Optional cap (default 100). */
  limit: z.string().optional(),
});
type ListQuery = z.infer<typeof listQuery>;

const listResponse = z.object({
  items: z.array(overrideQueueItemSchema),
});
type ListResponse = z.infer<typeof listResponse>;

const listOp: Operation<void, ListQuery, void, ListResponse> = {
  method: "GET",
  path: () => "/api/overrides/queue",
  query: listQuery,
  response: listResponse,
};

const overrideRecordSchema = z.object({
  sessionId: z.number().int().positive(),
  seq: z.number().int().nonnegative(),
  actorId: z.string(),
  path: z.string(),
  source: z.string().nullable(),
  subject: z.string().nullable(),
  presentedAs: z.enum(["diff", "raw"]).nullable(),
  wallMs: z.number().nullable(),
  createdAt: z.string(),
  before: z.unknown(),
  after: z.unknown(),
  changedKeys: z.array(z.string()),
  signature: z.string().nullable(),
  signatureStatus: z.enum(["signed", "unsigned", "invalid"]),
  reasonText: z.string().nullable(),
});
export type OverrideRecordItem = z.infer<typeof overrideRecordSchema>;

const recordsQuery = z.object({
  limit: z.string().optional(),
});
type RecordsQuery = z.infer<typeof recordsQuery>;

const recordsResponse = z.object({
  items: z.array(overrideRecordSchema),
});
type RecordsResponse = z.infer<typeof recordsResponse>;

const recordsOp: Operation<void, RecordsQuery, void, RecordsResponse> = {
  method: "GET",
  path: () => "/api/overrides/records",
  query: recordsQuery,
  response: recordsResponse,
};

export const overrides = {
  list: listOp,
  records: recordsOp,
};
